'use client'

import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { useProductsListFavoriteStatus } from '@/feature/product/hooks/useProductsListFavoriteStatus'
import {
  referencesProductArticlesAtom,
  referencesProductIsMonoDiscriminantAtom,
} from '@/feature/product/services/referencesProduct.store'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { useBreakpoints } from '@/lib/device/useBreakpoint'
import type {
  ProductAvailabilities_PickupPointOfSaleFragment,
  ProductBuyBox_CartFragment,
  ProductBuyBox_FavoritePointOfSaleFragment,
  ReferencesProduct_CurrentUserQuery,
  ReferencesProductTableRows_PriceAndStockQuery,
} from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import { createContext, useContext, useMemo, type ReactNode } from 'react'

type ReferencesProductTableContextValue = {
  // Config & features
  isPublicPriceEnabled: boolean
  isPublicStockEnabled: boolean
  isClickAndCollectEnabled: boolean
  isAuthenticated: boolean

  // Device
  isDesktopWidth: boolean

  // Product data
  isMonoDiscriminant: boolean
  isArticlesPriceAndStockLoading: boolean
  articlesPriceAndStock?: ReferencesProductTableRows_PriceAndStockQuery['productsByReferences']

  // User data
  favoritePointOfSale?: ProductBuyBox_FavoritePointOfSaleFragment | null
  cart?: ProductBuyBox_CartFragment
  favoriteMap?: Record<string, boolean>
  pickupPointOfSale?: ProductAvailabilities_PickupPointOfSaleFragment | null
}

const ReferencesProductTableContext = createContext<ReferencesProductTableContextValue | null>(null)

export type ReferencesProductTableProviderProps = {
  children: ReactNode
  value: ReferencesProductTableContextValue
}

export function ReferencesProductTableProvider({
  children,
  value,
}: ReferencesProductTableProviderProps) {
  return (
    <ReferencesProductTableContext.Provider value={value}>
      {children}
    </ReferencesProductTableContext.Provider>
  )
}

export function useReferencesProductTableContext() {
  const context = useContext(ReferencesProductTableContext)

  if (!context) {
    throw new Error(
      'useReferencesProductTableContext must be used within ReferencesProductTableProvider',
    )
  }

  return context
}

export type UseReferencesProductTableContextDataProps = {
  isArticlesPriceAndStockLoading: boolean
  currentUser?: ReferencesProduct_CurrentUserQuery['currentUser'] | null
  articlesPriceAndStock?: ReferencesProductTableRows_PriceAndStockQuery['productsByReferences']
  pickupPointOfSale?: ProductAvailabilities_PickupPointOfSaleFragment | null
}

export function useReferencesProductTableContextData({
  isArticlesPriceAndStockLoading,
  currentUser,
  articlesPriceAndStock,
  pickupPointOfSale,
}: UseReferencesProductTableContextDataProps): ReferencesProductTableContextValue {
  const articles = useAtomValue(referencesProductArticlesAtom)
  const isMonoDiscriminant = useAtomValue(referencesProductIsMonoDiscriminantAtom)

  const { config } = useSiteConfig()
  const { isDesktopWidth } = useBreakpoints()
  const isAuthenticated = useIsAuthenticated()

  const isPublicPriceEnabled = config?.catalog?.isPublicPricesEnabled ?? false
  const isPublicStockEnabled = config?.catalog?.isPublicStockEnabled ?? false
  const isClickAndCollectEnabled = config?.sales?.isClickAndCollectEnabled ?? false

  const { data: favoriteMap } = useProductsListFavoriteStatus(
    articles.map((article) => article.reference),
  )

  const cart = currentUser?.checkout?.cart

  return useMemo(
    () => ({
      isPublicPriceEnabled,
      isPublicStockEnabled,
      isClickAndCollectEnabled,
      isAuthenticated,
      isDesktopWidth,
      isMonoDiscriminant,
      isArticlesPriceAndStockLoading,
      articlesPriceAndStock,
      cart,
      favoriteMap,
      pickupPointOfSale,
    }),
    [
      isPublicPriceEnabled,
      isPublicStockEnabled,
      isClickAndCollectEnabled,
      isAuthenticated,
      isDesktopWidth,
      isMonoDiscriminant,
      isArticlesPriceAndStockLoading,
      articlesPriceAndStock,
      cart,
      favoriteMap,
      pickupPointOfSale,
    ],
  )
}
